import React, { useCallback, useEffect, useId, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import {
  ADVANCED_FILTER_FIELDS,
  ARRAY_NUMBER_FIELDS,
  STAFF_ONLY_FIELDS,
} from '@rover/constants/src/search/searchPage.constants';
import { Box, Button, Flex, Heading } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import { AdvancedFiltersCollection } from '@rover/react-lib/src/pages/search/SearchPage/components';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

const allFilterNames = [
  ...Array.from(ADVANCED_FILTER_FIELDS),
  ...Array.from(STAFF_ONLY_FIELDS).filter((field) => !ARRAY_NUMBER_FIELDS.has(field)),
  ...Array.from(ARRAY_NUMBER_FIELDS),
];

export type Props = {
  isOpen: boolean;
  onClose: (scrollToResults: boolean) => void;
  searchFilters: SearchFilters;
  updateFiltersAndFireSearchOnChange: (arg0: Partial<SearchFilters>) => void;
  priceFilterMinValue: number;
  priceFilterMaxValue: number;
};

const AdvancedFiltersBottomSheet = ({
  isOpen,
  onClose,
  searchFilters,
  updateFiltersAndFireSearchOnChange,
  priceFilterMinValue,
  priceFilterMaxValue,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const [localFilters, setLocalFilters] = useState<SearchFilters>(searchFilters);

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(searchFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleChange = useCallback((changes: Partial<SearchFilters>): void => {
    setLocalFilters((prev) => ({ ...prev, ...changes }));
  }, []);

  const handleResetAll = useCallback((): void => {
    setLocalFilters((prev) => {
      const resetFilters = { ...prev };
      allFilterNames.forEach((filterName) => {
        resetFilters[filterName] = Array.isArray(resetFilters[filterName]) ? [] : false;
      });
      resetFilters.starSitter = false;
      resetFilters.minprice = priceFilterMinValue;
      resetFilters.maxprice = priceFilterMaxValue;
      return resetFilters;
    });
  }, [priceFilterMinValue, priceFilterMaxValue]);

  const handleSubmit = (): void => {
    onClose(true);
    updateFiltersAndFireSearchOnChange(localFilters);
  };

  const handleClose = (): void => {
    onClose(false);
  };

  const headerTitleId = useId();
  return (
    <BottomSheet
      isOpen={isOpen}
      onRequestClose={handleClose}
      closeOnDragDown
      headerId={headerTitleId}
      overflowY="auto"
      contentPaddingStyles={{ padding: '0x' }}
    >
      <Flex justifyContent="space-between" alignItems="center" mb="2x" px="4x">
        <Heading id={headerTitleId} size="300" m="0x">
          {t`Filters`}
        </Heading>
        <Button
          variant="flat"
          size="small"
          onClick={handleResetAll}
          aria-label={t`Reset all filters`}
        >
          <Trans>Reset all</Trans>
        </Button>
      </Flex>

      <Box px="4x" pb="20x" flex="1">
        <AdvancedFiltersCollection formData={localFilters} onChange={handleChange} />
      </Box>
      <Box
        position="absolute"
        bottom="0"
        right="0"
        left="0"
        backgroundColor="white"
        p="4x"
        pb="6x"
        borderTop="1px solid"
        borderTopColor="separator.primary"
        zIndex="999"
      >
        <Button variant="primary" fullWidth onClick={handleSubmit} aria-label="Apply">
          <Trans>Apply</Trans>
        </Button>
      </Box>
    </BottomSheet>
  );
};

export default AdvancedFiltersBottomSheet;
